import { useState, useEffect, useRef } from 'react';
import Tabs from '../../tabs/tabs';
import TransactionStatusChange from '../incoming-outgoing-transaction-status-change';
import {
  IncomingTransactionGeneral,
  IncomingTransactionOrder,
  EnumTabs,
  BaseellaStatus,
  TransactionTypeEnum,
} from '../types';
import IncomingGeneralContent from './incoming-transaction-general';
import IncomingOrderContent from './incoming-transaction-order';
import IncomingOutgoingAmlContent from '../incoming-outgoing-transaction-aml';
import TransactionLogsContent from '../transaction-logs-content';
import TransactionWarnings from '../warnings/transation-warning';
import ChangeLogs from '../../change-logs/change-logs';
import TransactionSignatures from '../signatures/signatures';
import TransactionChats from '../chats/chats';
import TransactionDocuments from '../documents/documents';
import TransactionAccountingEntries from '../accounting-entries/accounting-entries';
import TransactionComments from '../comments/comments';
import IconAttention from '../../icons/attention';
import ButtonPrimary from '../../button/button-primary';
import ButtonSecondary from '../../button/button-secondary';
import { PermissionEnum } from '../../../base-types';
import formatDate from '../../../helpers/format-date';
import { SidepanelContentFormTypes } from './types';

import styles from '../transaction.module.css';

export default function IncomingTransactionSidepanelContentForm({
  statusesInfo,
  onRollbackStatus,
  onNextStatus,
  control,
  transactionInfo,
  amlInfo,
  currencies,
  recipientAccounts,
  updatePspPartnerBalance,
  pspPartnerBalanceIsLoading,
  pspPartnerAccountBalance,
  pspPartnerAccounts,
  pspPartners,
  feeText,
  onScrollToPaymentType,
  paymentsTypes,
  countries,
  errors,
  isEditable,
  onSearchAutocomplete,
  pspPartnersIsLoading,
  recipientAccountsIsLoading,
  pspPartnerAccountsIsLoading,
  pspPartnerIsFilled,
  paymentTypeIsFilled,
  baseellaStatus,
  logs,
  warnings,
  employee,
  paymentType,
  onApplyTransactionAction,
  changeLogs,
  isCreateForm,
  currencyId,
  updateBalance,
  accountBalance,
  balanceIsLoading,
  signatures,
  sendCode,
  setRejectPopupVisible,
  transferDate,
  transactionsTypes,
  setBlacklistDataVisible,
  setBlacklistData,
  setBlacklistItemVisible,
  setBlacklistDataItem,
  parentRef,
  parentScroll = 0,
  updateStatusIsLoading,
  isLoadingStatuses,
  activePermission,
  setAmount,
  amount,
  latestQueuedStatus,
  createdByOrganizationId,
  setValue,
  applyTransactionsWarningIsLoading,
  companyOfficials,
  chats,
  onCreateTransactionChat,
  chatCreateInProgress,
  currentEmployee,
  createChatWithMessage,
  documents,
  setSubPanelVisible,
  setSelectedDocumentName,
  setTransactionSidepanelVisible,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  selectedDocumentId,
  setSelectedDocumentId,
  downloadInProgress,
  tabs,
  activeTab,
  setActiveTab,
  glTransactionsIsLoading,
  glTransactions,
  setGlTransactionSidePanelVisible,
  onGlTransactionClick,
  onDownloadDocument,
  getTransactionDocument,
  canSignTransaction,
  sendCodeInProgress,
  balances,
  accountBalancesIsLoading,
  currenciesList,
  duplicate,
  fields,
  caspList,
  externalAccounts,
  externalAccountsIsLoading,
  updateExternalBalance,
  onSearchExternalAccounts,
  onClearExternalAccounts,
  onGetExternalAccountsListAction,
  externalAccountsPagination,
  externalAccountsNextPageIsLoading,
  transactionId,
  getTransactionDocuments,
  documentsIsLoading,
  getGeneralLedgerTransactionValues,
  getTransactionsChats,
  transactionChatsIsLoading,
  getCompanyOfficials,
  getSignaturesTransactionById,
  isLoadingSignatures,
  getIncomingTransactionByIdAml,
  isLoadingAml,
  getLogsTransactionById,
  isLoadingLogs,
  overdraftEnabled,
  recipientAccountsWithInfo,
  pspPartnersAccountsList,
  pspPartnerAccountBalanceInfo,
  getTransactionChangeLogs,
  changeLogsIsLoading,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  documentsPagination,
  documentsIsFetching,
  attachedDocumentsErrors,
  uploadedDocumentsErrors,
  transactionComments,
  transactionCommentsPagination,
  getTransactionCommentsAction,
  commentsIsLoading,
  commentsIsFetching,
  onAddCommentToTransaction,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInTransaction,
  editTransactionCommentInProgress,
  editTrancastionCommentIsSuccess,
  onDeleteCommentInTransaction,
  deleteCommentInTransactionInProgress,
  deleteCommentInTranscationIsSuccess,
  getIncomingTransactionWarningById,
  isWarningByIdFetching,
  isWarningByIdLoading,
  warningsInfo,
  selectedWarningId,
  pspPartnerAccountsPagination,
  pspPartnerAccountsNextPageIsLoading,
  pspPartnerAccountsOnScroll,
  warningActionError,
  clearErrors,
  setWithTime,
  withTime,
}: SidepanelContentFormTypes): React.JSX.Element {
  const [isChangedTab, setIsChangedTab] = useState<boolean>(false);
  const [signatureValid, setSignatureValid] = useState(true);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollToPaymentTypeField = () => {
    setActiveTab(EnumTabs.GENERAL);

    if (onScrollToPaymentType) {
      onScrollToPaymentType();
    }
  };

  useEffect(() => {
    if (
      !isChangedTab &&
      activeTab !== EnumTabs.DOCUMENTS &&
      activeTab !== EnumTabs.GL_TRANSACTIONS
    ) {
      setIsChangedTab(true);

      if (warningsInfo && warningsInfo?.entitySummary?.unprocessed > 0) {
        setActiveTab(EnumTabs.WARNING);
      } else {
        setActiveTab(EnumTabs.GENERAL);
      }
    }
  }, [warningsInfo, isChangedTab]);

  useEffect(() => {
    if (duplicate) {
      setActiveTab(EnumTabs.GENERAL);
    }
  }, [duplicate]);

  useEffect(() => {
    if (activeTab === EnumTabs.DOCUMENTS && getTransactionDocuments) {
      getTransactionDocuments();
    }

    if (
      activeTab === EnumTabs.GL_TRANSACTIONS &&
      getGeneralLedgerTransactionValues
    ) {
      getGeneralLedgerTransactionValues();
    }

    if (
      activeTab === EnumTabs.CHANGE_LOG &&
      getTransactionChangeLogs &&
      activePermission?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getTransactionChangeLogs();
    }

    if (
      (activeTab === EnumTabs.CHATS || activeTab === EnumTabs.WARNING) &&
      getTransactionsChats &&
      activePermission?.includes(PermissionEnum.CHANNELS_LIST)
    ) {
      getTransactionsChats();

      if (
        transactionInfo &&
        transactionInfo.recipientAccount &&
        transactionInfo.recipientAccount?.organization?.type === 'CORPORATE' &&
        getCompanyOfficials
      ) {
        getCompanyOfficials(transactionInfo.recipientAccount.organizationId);
      }
    }

    if (activeTab === EnumTabs.SIGNATURES && getSignaturesTransactionById) {
      getSignaturesTransactionById();
    }

    if (activeTab === EnumTabs.AML && getIncomingTransactionByIdAml) {
      getIncomingTransactionByIdAml();
    }

    if (
      activeTab === EnumTabs.LOG &&
      getLogsTransactionById &&
      activePermission?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INCOMING_LIST,
      )
    ) {
      getLogsTransactionById();
    }

    if (
      activeTab === EnumTabs.COMMENTS &&
      getTransactionCommentsAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_INCOMING_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }
  }, [activeTab, activePermission, transactionInfo]);

  useEffect(() => {
    if (
      (transactionInfo?.baseellaStatus === BaseellaStatus.MISSING_PAYER ||
        transactionInfo?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS) &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }
  }, [transactionInfo]);

  useEffect(() => {
    if (
      signatures &&
      signatures?.length > 0 &&
      (transactionInfo?.baseellaStatus === BaseellaStatus.MISSING_PAYER ||
        transactionInfo?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS)
    ) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const lastSignature = signatures[signatures?.length - 1];

      if (lastSignature) {
        setSignatureValid(lastSignature.valid as boolean);
      }
    }
  }, [signatures, transactionInfo]);

  return (
    <>
      {(baseellaStatus === BaseellaStatus.NOT_SIGNED ||
        ((baseellaStatus === BaseellaStatus.MISSING_PAYER ||
          baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS) &&
          !signatureValid)) &&
        canSignTransaction && (
          <div className={styles.notSignedAlert}>
            <div className={styles.notSignedText}>
              <IconAttention width={24} height={24} />
              Additional signatures required
            </div>
            <div className={styles.notSignedControls}>
              {(!createdByOrganizationId ||
                ((baseellaStatus === BaseellaStatus.MISSING_PAYER ||
                  baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS) &&
                  !signatureValid)) && (
                <ButtonPrimary
                  text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                  onClick={sendCode}
                  className={styles.signButton}
                  disabled={sendCodeInProgress}
                />
              )}
              {baseellaStatus === BaseellaStatus.NOT_SIGNED &&
                !createdByOrganizationId && (
                  <ButtonSecondary
                    text="Reject"
                    onClick={() =>
                      setRejectPopupVisible && setRejectPopupVisible(true)
                    }
                  />
                )}
            </div>
          </div>
        )}
      {statusesInfo && !transactionInfo?.deletedAt && (
        <TransactionStatusChange
          transaction={statusesInfo}
          onNextStatus={onNextStatus}
          baseellaStatus={baseellaStatus}
          onRollbackStatus={onRollbackStatus}
          updateStatusIsLoading={updateStatusIsLoading}
          isLoadingStatuses={isLoadingStatuses}
          latestQueuedStatus={latestQueuedStatus}
          activeTab={activeTab as EnumTabs}
        />
      )}
      {transactionInfo?.deletedAt && (
        <div className={styles.deleted}>{`Transaction deleted ${formatDate(
          transactionInfo?.deletedAt,
          true,
        )}`}</div>
      )}
      <Tabs
        tabs={tabs}
        onClick={(value) => {
          setActiveTab(value as EnumTabs);

          if (parentScroll >= Number(scrollRef.current?.offsetTop) - 130) {
            parentRef?.current?.scrollTo({
              top: Number(scrollRef.current?.offsetTop) - 130,
            });
          }
        }}
        activeTab={activeTab}
        hideWarningTabs={!warningsInfo?.entitySummary?.total}
        itemClassName={styles.transactionTab}
        warningsCount={warningsInfo?.entitySummary?.unprocessed}
        isTransactionsTabs
        // eslint-disable-next-line react-hooks/refs
        withShadow={parentScroll > Number(scrollRef.current?.offsetTop) - 130}
        moreButtonClassName={styles.transactionMoreButton}
        withSticky
      />
      <div
        ref={scrollRef}
        className={`${
          activeTab === EnumTabs.GL_TRANSACTIONS
            ? styles.sidepanelContentWrapper
            : ''
        } ${styles.siddepanelContentFlex}`}
      >
        <div
          className={
            activeTab === EnumTabs.GENERAL
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <IncomingGeneralContent
            isLoading={false}
            control={control}
            general={transactionInfo as IncomingTransactionGeneral}
            currencies={currencies}
            recipientAccounts={recipientAccounts}
            pspPartnerAccounts={pspPartnerAccounts}
            pspPartners={pspPartners}
            paymentsTypes={paymentsTypes}
            errors={errors}
            isEditable={isEditable}
            onSearchAutocomplete={onSearchAutocomplete}
            pspPartnersIsLoading={pspPartnersIsLoading}
            recipientAccountsIsLoading={recipientAccountsIsLoading}
            pspPartnerAccountsIsLoading={pspPartnerAccountsIsLoading}
            pspPartnerIsFilled={pspPartnerIsFilled}
            paymentTypeIsFilled={paymentTypeIsFilled}
            baseellaStatus={baseellaStatus}
            isCreateForm={isCreateForm}
            feeText={feeText}
            accountBalance={accountBalance}
            currencyId={currencyId}
            balanceIsLoading={balanceIsLoading}
            updateBalance={updateBalance}
            updatePspPartnerBalance={updatePspPartnerBalance}
            pspPartnerBalanceIsLoading={pspPartnerBalanceIsLoading}
            pspPartnerAccountBalance={pspPartnerAccountBalance}
            transferDate={transferDate}
            activePermission={activePermission}
            amount={amount}
            setAmount={setAmount}
            setValue={setValue}
            balances={balances}
            accountBalancesIsLoading={accountBalancesIsLoading}
            currenciesList={currenciesList}
            fields={fields}
            externalAccounts={externalAccounts}
            externalAccountsIsLoading={externalAccountsIsLoading}
            updateExternalBalance={updateExternalBalance}
            onSearchExternalAccounts={onSearchExternalAccounts}
            onClearExternalAccounts={onClearExternalAccounts}
            onGetExternalAccountsListAction={onGetExternalAccountsListAction}
            externalAccountsPagination={externalAccountsPagination}
            externalAccountsNextPageIsLoading={
              externalAccountsNextPageIsLoading
            }
            transactionId={transactionId}
            overdraftEnabled={overdraftEnabled}
            recipientAccountsWithInfo={recipientAccountsWithInfo}
            pspPartnersAccountsList={pspPartnersAccountsList}
            pspPartnerAccountBalanceInfo={pspPartnerAccountBalanceInfo}
            pspPartnerAccountsPagination={pspPartnerAccountsPagination}
            pspPartnerAccountsNextPageIsLoading={
              pspPartnerAccountsNextPageIsLoading
            }
            pspPartnerAccountsOnScroll={pspPartnerAccountsOnScroll}
            clearErrors={clearErrors}
            setWithTime={setWithTime}
            withTime={withTime}
          />
        </div>

        <div
          className={
            activeTab === EnumTabs.ORDER
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <IncomingOrderContent
            isLoading={false}
            control={control}
            order={transactionInfo as IncomingTransactionOrder}
            countries={countries}
            errors={errors}
            isEditable={isEditable}
            paymentType={paymentType}
            onScrollToPaymentTypeField={scrollToPaymentTypeField}
            fields={fields}
            caspList={caspList}
            currencyId={currencyId}
          />
        </div>

        {!isCreateForm && (
          <div
            className={
              activeTab === EnumTabs.WARNING
                ? styles.activeTabContent
                : styles.tabContent
            }
          >
            <TransactionWarnings
              warningsInfo={warningsInfo}
              warnings={warnings}
              employee={employee}
              onApplyTransactionAction={onApplyTransactionAction}
              transactionType={TransactionTypeEnum.INCOMING}
              transactionsTypes={transactionsTypes}
              setBlacklistDataVisible={setBlacklistDataVisible}
              setBlacklistData={setBlacklistData}
              setBlacklistItemVisible={setBlacklistItemVisible}
              setBlacklistDataItem={setBlacklistDataItem}
              applyTransactionsWarningIsLoading={
                applyTransactionsWarningIsLoading
              }
              companyOfficials={companyOfficials}
              individualCustomer={transactionInfo?.recipientAccount}
              chats={chats}
              createChatWithMessage={createChatWithMessage}
              chatCreateInProgress={chatCreateInProgress}
              activePermissions={activePermission}
              onResetTransactionWarningAction={onResetTransactionWarningAction}
              resetTransactionsWarningIsLoading={
                resetTransactionsWarningIsLoading
              }
              resetTransactionsWarningIsSuccess={
                resetTransactionsWarningIsSuccess
              }
              hiddenApprove={
                !activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_APPROVE_ACTION,
                )
              }
              hiddenReject={
                !activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_AML_REJECT_ACTION,
                )
              }
              getWarningById={getIncomingTransactionWarningById}
              isWarningByIdFetching={isWarningByIdFetching}
              isWarningByIdLoading={isWarningByIdLoading}
              selectedWarningId={selectedWarningId}
              warningActionError={warningActionError}
            />
          </div>
        )}
        {!isCreateForm && (
          <>
            <div
              className={
                activeTab === EnumTabs.AML
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <IncomingOutgoingAmlContent
                transactionType={TransactionTypeEnum.INCOMING}
                isLoading={isLoadingAml as boolean}
                aml={amlInfo}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionLogsContent
                isLoading={isLoadingLogs as boolean}
                logs={logs}
                activePermission={activePermission?.includes(
                  PermissionEnum.TRANSACTION_QUEUE_ITEMS_INCOMING_LIST,
                )}
              />
            </div>

            <div
              className={
                activeTab === EnumTabs.CHANGE_LOG
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <ChangeLogs
                entityType="TRANSACTION"
                entityId={transactionId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermission}
                users={users}
                pagination={transactionChangeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getTransactionChangeLogs}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.DOCUMENTS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionDocuments
                documents={documents}
                setSubPanelVisible={setSubPanelVisible}
                setSelectedDocumentName={setSelectedDocumentName}
                setTransactionSidepanelVisible={setTransactionSidepanelVisible}
                onUploadDocument={onUploadDocument}
                uploadDocumentInProgress={uploadDocumentInProgress}
                uploadDocumentIsSuccess={uploadDocumentIsSuccess}
                uploadDocumentErrors={uploadDocumentErrors}
                selectedDocumentId={selectedDocumentId}
                setSelectedDocumentId={setSelectedDocumentId}
                downloadInProgress={downloadInProgress}
                activePermission={activePermission}
                onDownloadDocument={onDownloadDocument}
                getTransactionDocument={getTransactionDocument}
                documentsIsLoading={documentsIsLoading}
                documentsPagination={documentsPagination}
                getTransactionDocuments={getTransactionDocuments}
                documentsIsFetching={documentsIsFetching}
                attachedDocumentsErrors={attachedDocumentsErrors}
                uploadedDocumentsErrors={uploadedDocumentsErrors}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.SIGNATURES
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionSignatures
                signatures={signatures}
                isLoadingSignatures={isLoadingSignatures}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.GL_TRANSACTIONS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionAccountingEntries
                glTransactionsIsLoading={glTransactionsIsLoading}
                glTransactions={glTransactions}
                setGlTransactionSidePanelVisible={
                  setGlTransactionSidePanelVisible
                }
                onGlTransactionClick={onGlTransactionClick}
                setTransactionSidepanelVisible={setTransactionSidepanelVisible}
                currenciesList={currenciesList}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.CHATS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionChats
                chats={chats}
                companyOfficials={companyOfficials}
                individualCustomer={transactionInfo?.recipientAccount}
                onCreateTransactionChat={onCreateTransactionChat}
                chatCreateInProgress={chatCreateInProgress}
                currentEmployee={currentEmployee}
                activePermissions={activePermission}
                transactionChatsIsLoading={transactionChatsIsLoading}
              />
            </div>
            <div
              className={
                activeTab === EnumTabs.COMMENTS
                  ? styles.activeTabContent
                  : styles.tabContent
              }
            >
              <TransactionComments
                comments={transactionComments}
                pagination={transactionCommentsPagination}
                isLoading={commentsIsLoading}
                isFetching={commentsIsFetching}
                commentsAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_COMMENTS_LIST,
                )}
                getCommentsList={getTransactionCommentsAction}
                createAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_COMMENTS_CREATE_ACTION,
                )}
                editAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_COMMENTS_EDIT_ACTION,
                )}
                deleteAvailable={activePermission?.includes(
                  PermissionEnum.TRANSACTIONS_INCOMING_COMMENTS_DELETE_ACTION,
                )}
                onAddCommentToTransaction={onAddCommentToTransaction}
                addCommentInProgress={addCommentInProgress}
                addCommentIsSuccess={addCommentIsSuccess}
                onEditCommentInTransaction={onEditCommentInTransaction}
                editTransactionCommentInProgress={
                  editTransactionCommentInProgress
                }
                editTrancastionCommentIsSuccess={
                  editTrancastionCommentIsSuccess
                }
                users={users}
                onDeleteCommentInTransaction={onDeleteCommentInTransaction}
                deleteCommentInTransactionInProgress={
                  deleteCommentInTransactionInProgress
                }
                deleteCommentInTranscationIsSuccess={
                  deleteCommentInTranscationIsSuccess
                }
                changeLogsAvailable={activePermission?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                )}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
